import React from "react";
import { useTranslation } from "react-i18next";
import { map } from "lodash";
import { DropdownMenu, DropdownMenuProps } from "@/shared/widgets/DropdownMenu";
import { IconButton } from "@/shared/ui/IconButton";
import { Column, Row } from "@/shared/ui/Blocks";
import { DropdownMenuItem } from "@/shared/ui/DropDown";
import { cn } from "@/shared/utils/shadcn";
import { Button } from "@/shared/ui/Button";
import { IconName } from "@/shared/ui/Icon";

export interface FilterItem {
  id: number | string;
  label: string;
  count?: number;
  isDefault?: boolean;
}

interface FiltersDropdownProps
  extends Omit<DropdownMenuProps, "trigger" | "items" | "onSelect"> {
  items: FilterItem[];
  type?: "single" | "multiple";
  selected?: string[];
  label?: string;
  icon?: IconName;
  onSelect: (id: string) => void;
  onClear?: () => void;
}

function FiltersDropdown(props: FiltersDropdownProps) {
  const {
    items,
    selected,
    label,
    icon,
    onSelect,
    onClear,
    type = "single",
    ...rest
  } = props;
  const { t } = useTranslation();

  const activeFiltersCount = selected?.length || 0;

  if (!items.length) return null;

  function FilterItems() {
    return (
      <Column className="p-2 gap-2">
        {map(items, (item) => (
          <DropdownMenuItem
            key={item.id}
            onClick={() => onSelect(item.id.toString())}
            className={cn(
              "cursor-pointer",
              selected?.includes(item.id.toString()) && "bg-grey-100"
            )}
          >
            <Row className="inline-flex items-center justify-between w-full p-2">
              <span>{item.label}</span>
              {item.count !== undefined && (
                <span className="text-grey-500 text-sm">({item.count})</span>
              )}
            </Row>
          </DropdownMenuItem>
        ))}
      </Column>
    );
  }

  function ClearFiltersButton() {
    if (!activeFiltersCount) return null;

    return (
      <DropdownMenuItem
        className="justify-center mt-4"
        onClick={onClear || (() => onSelect(""))}
      >
        <Button size="sm">{t("common.clear_filters")}</Button>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenu
      {...rest}
      items={[
        {
          label: label || t("common.filters"),
          renderItem: () => <FilterItems />,
        },
      ]}
      FooterComponent={<ClearFiltersButton />}
      trigger={
        <IconButton variant="outline" icon={icon || "Filters"} label={label}>
          {activeFiltersCount > 0 && (
            <span
              className={cn(
                "w-4 h-4 rounded-full bg-primary text-white text-xs flex items-center justify-center"
              )}
            >
              {activeFiltersCount}
            </span>
          )}
        </IconButton>
      }
      side="bottom"
    />
  );
}

export { FiltersDropdown };
